import { Auth, CognitoUser } from "@aws-amplify/auth";
import { captureException } from "@sentry/react";

import { IDeleteImageRequest, IResponse } from "../../types";
import { AWS_URL } from "../../utils/urlConstants";

const handleResponseStatusAndContentType = async (response: Response) => {
    const contentType = response.headers.get("content-type")!;

    if (response.status === 401) throw new Error("Request was not authorized.");

    if (contentType === null) return Promise.resolve(null);
    else if (contentType.startsWith("application/json")) return response.json();
    else if (contentType.startsWith("text/plain")) return response.text();
    else throw new Error(`Unsupported response content-type: ${contentType}`);
};

const getAccessToken = async () => {
    return (await Auth.currentSession()).getIdToken().getJwtToken();
};

const refreshJWT = async () => {
    try {
        const user: CognitoUser = await Auth.currentAuthenticatedUser();
        const session = await Auth.currentSession();
        user.refreshSession(session.getRefreshToken(), () => {});
        return true;
    } catch (e) {
        captureException(e);
        return false;
    }
};

export const doPut = async (
    path: string,
    isAuth: boolean,
    formData?: FormData
): Promise<IResponse> => {
    let authToken: string | undefined;
    const headers: HeadersInit = {};

    if (isAuth) {
        authToken = await getAccessToken();

        if (!authToken) {
            return { error: "User is not logged in." };
        }

        headers["Authorization"] = `Bearer ${authToken}`;
    }

    try {
        const response = await fetch(`${AWS_URL}${path}`, {
            method: "PUT",
            headers,
            body: formData,
        });

        const status = response.status;

        switch (status) {
            case 200:
            case 204:
                const data = await handleResponseStatusAndContentType(response);
                return { data };
            case 401:
                if (await refreshJWT()) {
                    return await doPut(path, isAuth, formData);
                } else {
                    const err = await response.json();
                    captureException(err);
                    return { error: err };
                }
            default:
                const err = await response.json();
                return { error: err };
        }
    } catch (error) {
        console.error(`Network error in doPut for path ${path}:`, error);
        return {
            error: "Unable to connect to server. Please try again later.",
        };
    }
};

export const doPutJson = async (
    path: string,
    isAuth: boolean,
    dataPayload: object
): Promise<IResponse> => {
    let authToken: string | undefined;
    const headers: HeadersInit = {};

    if (isAuth) {
        authToken = await getAccessToken();

        if (!authToken) {
            return { error: "User is not logged in." };
        }

        headers["Authorization"] = `Bearer ${authToken}`;
    }

    headers["Content-Type"] = "application/json";

    const response = await fetch(`${AWS_URL}${path}`, {
        method: "PUT",
        headers,
        body: JSON.stringify(dataPayload),
    });

    const status = response.status;

    switch (status) {
        case 200:
        case 204:
            const data = await handleResponseStatusAndContentType(response);
            return { data };
        case 401:
            if (await refreshJWT()) {
                return await doPutJson(path, isAuth, dataPayload);
            } else {
                const err = await response.json();
                captureException(err);
                return { error: err };
            }
        default:
            const err = await response.json();
            return { error: err };
    }
};

export const doPost = async (
    path: string,
    isAuth: boolean,
    formData?: FormData
): Promise<IResponse> => {
    let authToken: string | undefined;
    const headers: HeadersInit = {};

    if (isAuth) {
        authToken = await getAccessToken();

        if (!authToken) {
            return { error: "User is not logged in." };
        }

        headers["Authorization"] = `Bearer ${authToken}`;
    }

    const response = await fetch(`${AWS_URL}${path}`, {
        method: "POST",
        headers,
        body: formData,
    });

    const status = response.status;

    switch (status) {
        case 200:
            const data = await handleResponseStatusAndContentType(response);
            return { data };
        case 201:
            return { data: true };
        case 401:
            if (await refreshJWT()) {
                return await doPost(path, isAuth, formData);
            } else {
                const err = await response.json();
                captureException(err);
                return { error: err };
            }
        default:
            const err = await response.json();
            return { error: err };
    }
};

export const doPostJson = async (
    path: string,
    isAuth: boolean,
    dataPayload: object
): Promise<IResponse> => {
    let authToken: string | undefined;
    const headers: HeadersInit = {};

    if (isAuth) {
        authToken = await getAccessToken();

        if (!authToken) {
            return { error: "User is not logged in." };
        }

        headers["Authorization"] = `Bearer ${authToken}`;
    }

    headers["Content-Type"] = "application/json";

    try {
        const response = await fetch(`${AWS_URL}${path}`, {
            method: "POST",
            headers,
            body: JSON.stringify(dataPayload),
        });

        const status = response.status;

        switch (status) {
            case 200:
                const data = await handleResponseStatusAndContentType(response);
                return { data };
            case 201:
                return { data: true };
            case 202:
                return { data: true };
            case 204:
                return { data: true };
            case 401:
                if (await refreshJWT()) {
                    return await doPostJson(path, isAuth, dataPayload);
                } else {
                    const err = await response.json();
                    captureException(err);
                    return { error: err };
                }
            default:
                const err = await response.json();
                return { error: err };
        }
    } catch (error) {
        console.error(`Network error in doPostJson for path ${path}:`, error);
        return {
            error: "Unable to connect to server. Please try again later.",
        };
    }
};

export const doPostJsonOptAuth = async (
    path: string,
    dataPayload: object
): Promise<IResponse> => {
    try {
        await Auth.currentAuthenticatedUser(); // Check if user is authenticated
        return doPostJson(path, true, dataPayload); // Assume authenticated
    } catch (error) {
        return doPostJson(path, false, dataPayload); // Assume not authenticated
    }
};

export const doGetOptAuth = async (path: string): Promise<IResponse> => {
    try {
        await Auth.currentAuthenticatedUser(); // Check if user is authenticated
        return doGet(path, true); // Assume authenticated
    } catch (error) {
        return doGet(path, false); // Assume not authenticated
    }
};

export const doGet = async (
    path: string,
    isAuth: boolean
): Promise<IResponse> => {
    let authToken: string | undefined;
    const headers: HeadersInit = {};

    if (isAuth) {
        authToken = await getAccessToken();

        if (!authToken) {
            return { error: "User is not logged in." };
        }

        headers["Authorization"] = `Bearer ${authToken}`;
    }

    try {
        const response = await fetch(`${AWS_URL}${path}`, {
            method: "GET",
            headers,
        });

        const status = response.status;

        switch (status) {
            case 200:
                const data = await handleResponseStatusAndContentType(response);
                return { data };
            case 202:
                return {};
            case 401:
                if (await refreshJWT()) {
                    return await doGet(path, isAuth);
                } else {
                    try {
                        const err = await response.json();
                        return { error: err };
                    } catch (e) {
                        return {
                            error: {
                                status: 401,
                                message: "Unauthorized",
                            },
                        };
                    }
                }
            case 404:
                return { error: { status: 404, message: "Not found" } };
            default:
                try {
                    const err = await response.json();
                    return { error: err };
                } catch (e) {
                    return {
                        error: {
                            status: response.status,
                            message: response.statusText || "Unknown error",
                        },
                    };
                }
        }
    } catch (error) {
        console.error(`Network error in doGet for path ${path}:`, error);
        return {
            error: "Unable to connect to server. Please try again later.",
        };
    }
};

export const doDelete = async (
    path: string,
    isAuth: boolean
): Promise<IResponse> => {
    let authToken: string | undefined;
    const headers: HeadersInit = {};

    if (isAuth) {
        authToken = await getAccessToken();

        if (!authToken) {
            return { error: "User is not logged in." };
        }

        headers["Authorization"] = `Bearer ${authToken}`;
    }

    try {
        const response = await fetch(`${AWS_URL}${path}`, {
            method: "DELETE",
            headers,
        });

        const status = response.status;

        switch (status) {
            case 200:
            case 202:
            case 204:
                return {};
            case 401:
                if (await refreshJWT()) {
                    return await doDelete(path, isAuth); // Retry delete on refresh
                } else {
                    const err = await response.json();
                    return { error: err };
                }
            default:
                const err = await response.json();
                return { error: err };
        }
    } catch (error) {
        console.error(`Network error in doDelete for path ${path}:`, error);
        return {
            error: "Unable to connect to server. Please try again later.",
        };
    }
};

export const doDeleteWithJson = async (
    path: string,
    isAuth: boolean,
    dataPayload: IDeleteImageRequest
): Promise<IResponse> => {
    let authToken: string | undefined;
    const headers: HeadersInit = {
        "Content-Type": "application/json",
    };

    if (isAuth) {
        authToken = await getAccessToken();

        if (!authToken) {
            return { error: "User is not logged in." };
        }

        headers["Authorization"] = `Bearer ${authToken}`;
    }

    const response = await fetch(`${AWS_URL}${path}`, {
        method: "DELETE",
        body: JSON.stringify(dataPayload),
        headers,
    });

    const status = response.status;

    switch (status) {
        case 200:
        case 202:
        case 204:
            return {};
        case 401:
            if (await refreshJWT()) {
                return await doDeleteWithJson(path, isAuth, dataPayload); // Retry delete on refresh
            } else {
                const err = await response.json();
                return { error: err };
            }
        default:
            const err = await response.json();
            return { error: err };
    }
};
